import { filtersToCss, isDefault, type FiltersState } from './filterState';

/**
 * Bucket 5 — bake CSS filters into a saved asset.
 *
 * Pure-ish helpers for the FilterPanel's "Apply Filters" button. The
 * algorithm is the one the spec calls out: load an offscreen canvas
 * sized to the source's natural dims, set `ctx.filter = filtersToCss(state)`,
 * draw the image, and ship the resulting bytes through the existing
 * `assetService.uploadImage` pipeline.
 *
 * Kept in its own module so:
 *   1. The math is unit-testable without React Testing Library (RTL devdep
 *      hasn't landed yet — F-T4-component will bring it).
 *   2. Future v3.1 work can swap the canvas backend (e.g. `OffscreenCanvas`
 *      in a worker for very large images) without touching FilterPanel.
 */

/**
 * Read-the-tin contract for the canvas-2D side of the bake. Tests inject a
 * fake context that records the draw sequence; production passes a real
 * `CanvasRenderingContext2D`.
 */
export interface BakeDrawContext {
  canvas: { width: number; height: number };
  filter: string;
  drawImage: (
    image: CanvasImageSource,
    dx: number,
    dy: number,
    dw: number,
    dh: number,
  ) => void;
}

export type CanvasFactory = (width: number, height: number) => {
  context: BakeDrawContext;
  toBlob: (mimeType: string, quality?: number) => Promise<Blob | null>;
};

/**
 * Default canvas factory — uses `document.createElement('canvas')` in the
 * browser. In jsdom (test env), `canvas.getContext` returns null, so this
 * factory throws a recognizable error that callers can surface.
 *
 * Skipped at import time on environments without `document` (the helpers
 * can still be imported and unit-tested via the injected factory path).
 */
export const browserCanvasFactory: CanvasFactory = (width, height) => {
  if (typeof document === 'undefined') {
    throw new Error('Canvas factory requires a DOM (document is not defined).');
  }
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext('2d');
  if (!context) {
    throw new Error('Browser did not provide a 2D canvas context.');
  }
  return {
    context: context as unknown as BakeDrawContext,
    toBlob: (mimeType, quality) =>
      new Promise(resolve => {
        // canvas.toBlob signature varies; the callback may receive null.
        canvas.toBlob(blob => resolve(blob), mimeType, quality);
      }),
  };
};

/**
 * Resolve the image's intrinsic dimensions. Some callers will pass an
 * HTMLImageElement that hasn't loaded yet — return null so the calling
 * helper can throw a typed error rather than silently producing a 0x0
 * canvas.
 */
export interface BakeImageSource {
  /** Must be loaded (naturalWidth/naturalHeight > 0). */
  image: CanvasImageSource & { naturalWidth?: number; naturalHeight?: number };
  naturalWidth?: number;
  naturalHeight?: number;
}

export function resolveBakeDimensions(source: BakeImageSource): { width: number; height: number } | null {
  const w =
    source.naturalWidth ??
    ('naturalWidth' in source.image ? source.image.naturalWidth : undefined);
  const h =
    source.naturalHeight ??
    ('naturalHeight' in source.image ? source.image.naturalHeight : undefined);
  if (!w || !h || !Number.isFinite(w) || !Number.isFinite(h) || w <= 0 || h <= 0) {
    return null;
  }
  return { width: w, height: h };
}

export interface BakeResult {
  blob: Blob;
  mimeType: string;
  width: number;
  height: number;
  filterCss: string;
}

export interface BakeOptions {
  source: BakeImageSource;
  state: FiltersState;
  mimeType?: string;       // default 'image/png'
  quality?: number;        // canvas.toBlob quality (lossy codecs)
  factory?: CanvasFactory; // default browserCanvasFactory
}

/**
 * Render `source` through `state` into a Blob via an offscreen canvas.
 * Throws when state is neutral (`isDefault`) — callers should check first
 * so they don't generate an asset identical to the source. Throws when the
 * canvas backend returns null bytes (e.g. tainted canvas / OOM).
 */
export async function bakeFiltersToBlob(opts: BakeOptions): Promise<BakeResult> {
  if (isDefault(opts.state)) {
    throw new Error('Filters are at their neutral state; nothing to bake.');
  }
  const dims = resolveBakeDimensions(opts.source);
  if (!dims) {
    throw new Error('Source image has no intrinsic dimensions — it may still be loading.');
  }

  const mimeType = opts.mimeType ?? 'image/png';
  const factory = opts.factory ?? browserCanvasFactory;
  const surface = factory(dims.width, dims.height);

  surface.context.filter = filtersToCss(opts.state);
  surface.context.drawImage(opts.source.image, 0, 0, dims.width, dims.height);

  const blob = await surface.toBlob(mimeType, opts.quality);
  if (!blob) {
    throw new Error('Canvas produced no bytes (image may be cross-origin tainted).');
  }
  return {
    blob,
    mimeType,
    width: dims.width,
    height: dims.height,
    filterCss: surface.context.filter,
  };
}

/**
 * File-name composer for the baked asset. The upload service treats the
 * filename as a hint (the server assigns its own UUID), but a meaningful
 * name surfaces nicely in the gallery preview.
 */
export function bakedFilterFilename(prefix: string = 'filtered'): string {
  // Compact ISO timestamp — sortable, no colons that break filesystems.
  const ts = new Date().toISOString().replace(/[:.]/g, '-');
  return `${prefix}-${ts}.png`;
}
